import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import {
  DRAWER_PARAM_KEYS,
  getCleanDrawerParams,
  queryKeys,
  queryValues,
} from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useEffect, useMemo } from 'react';
import { getEarningsInsightParentDrawerParams } from './utils';

export enum SubTabsType {
  Preview = 'earnings_preview',
  Recap = 'earnings_recap',
}

const trackedDrawers = new Set<string>();

interface EarningsInsightDrawerOpenOptions {
  entityTag: string;
  earningsId: string;
  insightId?: string;
  subtab?: SubTabsType;
  source: PosthogTrackingSource;
}

export interface UsEarningsInsightDrawerReturn {
  closeDrawer: () => void;
  openDrawer: (options: EarningsInsightDrawerOpenOptions) => void;
  switchTab: (index: number) => void;
  isDrawerOpen: boolean;
  activeEntityTag: string | undefined;
  activeTab: SubTabsType;
  earningsId: string | undefined;
}

export const useEarningsInsightDrawer = () => {
  const { deleteQueryParam, setQueryParams, params } = useQueryParams();
  const { trackEarningsInsightDrawerOpen } = usePosthogEvents();
  const isDrawerOpen =
    params[queryKeys.activeDrawer] === queryValues.earningsInsightDrawer;
  const activeEntityTag = params[queryKeys.entityTag];
  const earningsId = params[queryKeys.earningsId];

  const drawerId = useMemo(() => {
    return activeEntityTag && earningsId
      ? `${activeEntityTag}-${earningsId}`
      : null;
  }, [activeEntityTag, earningsId]);

  const subtabs = [SubTabsType.Preview, SubTabsType.Recap];
  const foundTabIndex = subtabs.findIndex(
    tab => tab === params[queryKeys.activeDrawerTab]
  );
  const activeTabIndex = foundTabIndex !== -1 ? foundTabIndex : 0;
  const activeTab = subtabs[activeTabIndex];

  const openDrawer = ({
    entityTag,
    earningsId,
    insightId,
    subtab,
    source,
  }: EarningsInsightDrawerOpenOptions) => {
    const drawerType = subtab ?? SubTabsType.Preview;
    const parentThemeId =
      params[queryKeys.activeDrawer] === queryValues.themeEarningsDrawer ||
      params[queryKeys.parentDrawer] === queryValues.themeEarningsDrawer
        ? (params[queryKeys.themeId] ?? undefined)
        : undefined;

    trackEarningsInsightDrawerOpen({
      source,
      type: drawerType,
      insightId: earningsId,
      entities: [entityTag],
    });
    setQueryParams({
      ...getCleanDrawerParams(),
      [queryKeys.activeDrawer]: queryValues.earningsInsightDrawer,
      [queryKeys.activeDrawerTab]: drawerType,
      [queryKeys.entityTag]: entityTag,
      [queryKeys.earningsId]: earningsId,
      [queryKeys.insightId]: insightId,
      [queryKeys.parentDrawer]: parentThemeId
        ? queryValues.themeEarningsDrawer
        : undefined,
      [queryKeys.themeId]: parentThemeId,
    });
  };

  const closeDrawer = () => {
    const parentDrawerParams = getEarningsInsightParentDrawerParams(params);
    if (parentDrawerParams) {
      setQueryParams(parentDrawerParams);
      return;
    }

    deleteQueryParam([...DRAWER_PARAM_KEYS, queryKeys.drawerExpanded]);
  };

  const switchTab = (index: number) => {
    setQueryParams({ [queryKeys.activeDrawerTab]: subtabs[index] });
  };

  const utmSource = params[queryKeys.utmSource];

  useEffect(() => {
    if (!isDrawerOpen || !drawerId || !utmSource) {
      if (!isDrawerOpen && drawerId && trackedDrawers.has(drawerId)) {
        trackedDrawers.delete(drawerId);
      }

      return;
    }

    if (trackedDrawers.has(drawerId)) {
      return;
    }

    if (!earningsId || !activeEntityTag) {
      return;
    }

    trackedDrawers.add(drawerId);

    trackEarningsInsightDrawerOpen({
      source: utmSource as PosthogTrackingSource,
      type: activeTab,
      insightId: earningsId,
      entities: [activeEntityTag],
    });
  }, [
    activeEntityTag,
    activeTab,
    drawerId,
    earningsId,
    isDrawerOpen,
    utmSource,
  ]);

  return {
    closeDrawer,
    openDrawer,
    isDrawerOpen,
    activeEntityTag,
    switchTab,
    activeTab,
    earningsId,
  };
};
